import { useAppSelector } from '@hooks/redux.hooks';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { selectSensorRoleActionsFromActiveProfile } from '@redux/authent/authent.selectors';
import { selectActiveGroupsPlatforms } from '@redux/config/config.selectors';
import { updateSelectedMonitoringSensorUniqueKey } from '@redux/global/global.reducer';
import { selectSelectedMonitoringSensorUniqueKey } from '@redux/global/global.selector';
import { selectAllMonitoringSensors, selectAllSensorOrSubSensorInfo } from '@redux/situation/monitoring.selectors';
import { getPlatformsCodesFromMarks, isSetEqual } from '@utils/common.utils';
import { isSensorWithSubSensors } from '@utils/map/sensor.utils';
import { CategoryEnum, sensorFamilyFromSensorType } from '@utils/sensors/configuration.constants';
import { getSubSensorAndSensorConfigurationAndStatus, isSubSensorConfiguration } from '@utils/sensors/sensors.utils';
import { Polygon } from 'geojson';
import { isEqual } from 'lodash';
import { createContext, Dispatch, FC, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { ActionEnum } from '@/types/commons/commons.types';
import { Platform } from '@/types/config/config.types';
import { ActiveMonitoringSensor, InactiveMonitoringSensor } from '@/types/sensor/monitoring.types';
import { SensorFamilyEnum } from '@/types/sensor/sensor.types';
import { SensorTypeEnum, SubSensorTypeEnum } from '@/types/sensor/sensors';

import {
  CriticalityCategoryEnum,
  MonitoringLadTypeEnum,
  MonitoringPerimTypeEnum,
  monitoringTypeFromSensorType,
  SensorOrSubSensorInfo,
} from './monitoring.types';
import { getSensorCriticality, isLadType, isPerimType } from './monitoring.utils';
import { useMonitoringReducer, UseMonitoringReducerReturn } from './useMonitoringReducer';

export enum SortEnum {
  CRITICALITY = 'CRITICALITY',
  TYPE = 'TYPE',
}

export type MonitoringFilter = {
  name: string;
  categories: Map<CategoryEnum, boolean>;
  families: Map<SensorFamilyEnum, boolean>;
  ladTypes: Map<MonitoringLadTypeEnum, boolean>;
  perimTypes: Map<MonitoringPerimTypeEnum, boolean>;
  statusCategories: Set<CriticalityCategoryEnum>;
  showParent: boolean;
  showChild: boolean;
};

export type MonitoringState = {
  open: boolean;
  selectedSensorKey: string | null;
  sort: SortEnum;
} & MonitoringFilter;

type MonitoringContextType = {
  isSynchronizing: boolean;
  defaultFilters: MonitoringFilter;
  countByStatusFilter: Record<CriticalityCategoryEnum, number>;
  numberOfChanges: number;
  filteredSensorOrSubSensorInfoList: SensorOrSubSensorInfo[];
  platformFilters: { platform: Platform; active: boolean }[];
  setPlatformFilters: Dispatch<{ platform: Platform; active: boolean }[]>;
} & UseMonitoringReducerReturn;

const MonitoringContext = createContext<MonitoringContextType | null>(null);

function countChange(defaultState: MonitoringFilter, currentState: MonitoringState): number {
  let countChange = 0;
  if (!isEqual(defaultState.ladTypes, currentState.ladTypes)) {
    countChange += 1;
  }
  if (!isEqual(defaultState.perimTypes, currentState.perimTypes)) {
    countChange += 1;
  }
  if (!isEqual(defaultState.categories, currentState.categories)) {
    countChange += 1;
  }
  if (defaultState.name !== currentState.name) {
    countChange += 1;
  }
  if (defaultState.showChild !== currentState.showChild || defaultState.showParent !== currentState.showParent) {
    countChange += 1;
  }
  if (!isEqual(defaultState.families, currentState.families)) {
    countChange += 1;
  }
  if (!isSetEqual(defaultState.statusCategories, currentState.statusCategories)) {
    countChange += 1;
  }
  return countChange;
}

export const MonitoringProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { formatMessage } = useIntl();
  const sensorActions = useAppSelector(selectSensorRoleActionsFromActiveProfile);
  const platforms = useAppSelector(selectActiveGroupsPlatforms);
  const monitoringSensors = useAppSelector(selectAllMonitoringSensors);
  const selectedMonitoringSensorUniqueKey = useAppSelector(selectSelectedMonitoringSensorUniqueKey);
  const dispatchSelectedMonitoringSensorUniqueKey = useWithDispatch(updateSelectedMonitoringSensorUniqueKey);
  const sensorsOrsubSensors = useAppSelector(selectAllSensorOrSubSensorInfo);
  const hasSensorWithoutPlatform = monitoringSensors.some(
    (monitoringSensor) => getPlatformsCodesFromMarks(monitoringSensor.configuration.marks).length === 0,
  );

  const [isSynchronizing, setIsSynchronizing] = useState(true);
  const [platformFilters, setPlatformFilters] = useState<{ platform: Platform; active: boolean }[]>([]);

  useEffect(() => {
    const withoutPlatform = {
      id: -1,
      code: '',
      site: '',
      name: formatMessage({ id: 'components.monitoring.header.withoutPlatform' }),
      altitude: 0,
      heightOffset: 0,
      polygon: {
        type: 'Polygon',
        coordinates: [],
      } as Polygon,
      contacts: [],
      messagingParameters: {
        nfzEntry: {
          enabled: false,
          levels: [],
        },
        nfzExit: {
          enabled: false,
          levels: [],
          delay: 0,
        },
        droneLost: {
          enabled: false,
        },
      },
    };
    const platformList = hasSensorWithoutPlatform ? [...platforms, withoutPlatform] : platforms;

    setPlatformFilters((currentFilters) => {
      const currentById = new Map(currentFilters.map((filter) => [filter.platform.id, filter]));

      return platformList.map((platform) => ({
        platform,
        active: currentById.get(platform.id)?.active ?? true,
      }));
    });
  }, [platforms, hasSensorWithoutPlatform, formatMessage]);

  const filterByPlatform = useCallback(
    (monitoringSensor: ActiveMonitoringSensor | InactiveMonitoringSensor) => {
      const platforms = getPlatformsCodesFromMarks(monitoringSensor.configuration.marks);
      if (platforms.length === 0) {
        return platformFilters.find((platformFilter) => platformFilter.platform.id === -1)?.active;
      }
      return platformFilters.some(
        (platformFilter) => platforms.includes(platformFilter.platform.code) && platformFilter.active,
      );
    },
    [platformFilters],
  );

  const sensorFamilies = useMemo(
    () =>
      Array.from(sensorActions)
        .filter((sensorAction) => sensorAction.action === ActionEnum.ACCESS)
        .map((sensorAction) => sensorAction.sensorFamily)
        .filter((family) =>
          monitoringSensors.some((monitoringSensor) => sensorFamilyFromSensorType[monitoringSensor.type] === family),
        )
        .sort(
          (family1, family2) =>
            Object.values(SensorFamilyEnum).indexOf(family1) - Object.values(SensorFamilyEnum).indexOf(family2),
        ),
    [sensorActions, monitoringSensors],
  );

  const monitoringSensorTypes = useMemo(
    () =>
      Object.keys(monitoringTypeFromSensorType)
        .filter((type) =>
          Array.from(sensorFamilies).includes(sensorFamilyFromSensorType[type as SensorTypeEnum | SubSensorTypeEnum]),
        )
        .filter((type) =>
          sensorsOrsubSensors.some((sensorsOrsubsensor) => sensorsOrsubsensor.configuration.type === type),
        )
        .map((type) => monitoringTypeFromSensorType[type as SensorTypeEnum | SubSensorTypeEnum]),
    [sensorFamilies, sensorsOrsubSensors],
  );

  const getCategories = useCallback(() => {
    const types: Map<CategoryEnum, boolean> = new Map();
    if (monitoringSensorTypes.some(isLadType)) {
      types.set(CategoryEnum.LAD, false);
    }
    if (monitoringSensorTypes.some(isPerimType)) {
      types.set(CategoryEnum.PERIM, false);
    }
    return types;
  }, [monitoringSensorTypes]);

  const defaultFilters: MonitoringFilter = useMemo(
    () => ({
      name: '',
      categories: getCategories(),
      families: new Map(sensorFamilies.map((family) => [family, false])),
      ladTypes: new Map(
        monitoringSensorTypes
          .filter(isLadType)
          .sort(
            (category1, category2) =>
              Object.values(MonitoringLadTypeEnum).indexOf(category1) -
              Object.values(MonitoringLadTypeEnum).indexOf(category2),
          )
          .map((category) => [category, false]),
      ),
      perimTypes: new Map(
        monitoringSensorTypes
          .filter(isPerimType)
          .sort(
            (category1, category2) =>
              Object.values(MonitoringPerimTypeEnum).indexOf(category1) -
              Object.values(MonitoringPerimTypeEnum).indexOf(category2),
          )
          .map((category) => [category, false]),
      ),
      statusCategories: new Set(Object.values(CriticalityCategoryEnum)),
      showParent: false,
      showChild: false,
    }),
    [getCategories, monitoringSensorTypes, sensorFamilies],
  );

  const initialState: MonitoringState = {
    open: true,
    selectedSensorKey: selectedMonitoringSensorUniqueKey,
    sort: SortEnum.CRITICALITY,
    ...defaultFilters,
    categories: new Map(defaultFilters.categories),
    families: new Map(defaultFilters.families),
    ladTypes: new Map(defaultFilters.ladTypes),
    perimTypes: new Map(defaultFilters.perimTypes),
  };

  const { state, ...actions } = useMonitoringReducer(initialState, defaultFilters);

  useEffect(() => {
    actions.syncFilters(defaultFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultFilters]);

  const numberOfChanges = countChange(defaultFilters, state);

  const filterByStatus = useCallback(
    (sensorOrSubSensorInfo: SensorOrSubSensorInfo, statusCategories: Set<CriticalityCategoryEnum>) => {
      const criticality = getSensorCriticality(sensorOrSubSensorInfo);
      return statusCategories.has(criticality);
    },
    [],
  );

  const filterByName = useCallback(
    (sensor: SensorOrSubSensorInfo) => {
      return sensor.configuration.name.toLowerCase().includes(state.name.toLowerCase());
    },
    [state.name],
  );

  const filterByComplexity = useCallback(
    (sensor: SensorOrSubSensorInfo) => {
      const { showChild, showParent } = state;
      if (!showChild && !showParent) {
        return true;
      }
      if (isSubSensorConfiguration(sensor.configuration)) {
        return showChild;
      }
      return !isSensorWithSubSensors(sensor.configuration) || showParent;
    },
    [state],
  );

  const filterByMonitoringSensorType = useCallback(
    (sensorOrSubSensorInfo: SensorOrSubSensorInfo) => {
      const type = sensorOrSubSensorInfo.type;
      if (
        Array.from(state.ladTypes.values()).every((value) => !value) &&
        Array.from(state.perimTypes.values()).every((value) => !value)
      ) {
        return true;
      }
      const monitoringSensorType = monitoringTypeFromSensorType[type];
      return (
        (isLadType(monitoringSensorType) && state.ladTypes.get(monitoringSensorType)) ||
        (isPerimType(monitoringSensorType) && state.perimTypes.get(monitoringSensorType))
      );
    },
    [state.ladTypes, state.perimTypes],
  );

  const filteredSensorOrSubSensorInfoListWithoutStatus: SensorOrSubSensorInfo[] = useMemo(() => {
    return monitoringSensors
      .filter(filterByPlatform)
      .flatMap(getSubSensorAndSensorConfigurationAndStatus)
      .filter(filterByName)
      .filter(filterByComplexity)
      .filter(filterByMonitoringSensorType);
  }, [monitoringSensors, filterByPlatform, filterByName, filterByComplexity, filterByMonitoringSensorType]);

  const filteredSensorOrSubSensorInfoList: SensorOrSubSensorInfo[] = useMemo(() => {
    return filteredSensorOrSubSensorInfoListWithoutStatus.filter((sensor) =>
      filterByStatus(sensor, state.statusCategories),
    );
  }, [filteredSensorOrSubSensorInfoListWithoutStatus, filterByStatus, state.statusCategories]);

  const countByStatusFilter = useMemo(() => {
    return Object.values(CriticalityCategoryEnum).reduce(
      (acc, statusCategory) => {
        return {
          ...acc,
          [statusCategory]: filteredSensorOrSubSensorInfoListWithoutStatus.filter((sensor) =>
            filterByStatus(sensor, new Set<CriticalityCategoryEnum>([statusCategory])),
          ).length,
        };
      },
      {} as Record<CriticalityCategoryEnum, number>,
    );
  }, [filterByStatus, filteredSensorOrSubSensorInfoListWithoutStatus]);

  useEffect(() => {
    setIsSynchronizing(false);
  }, [platforms, monitoringSensors]);

  const value = useMemo(
    () => ({
      state,
      ...actions,
      isSynchronizing,
      countByStatusFilter,
      filteredSensorOrSubSensorInfoList,
      numberOfChanges,
      defaultFilters,
      setPlatformFilters,
      platformFilters,
    }),
    [
      state,
      actions,
      isSynchronizing,
      countByStatusFilter,
      filteredSensorOrSubSensorInfoList,
      numberOfChanges,
      defaultFilters,
      platformFilters,
    ],
  );

  useEffect(() => {
    if (selectedMonitoringSensorUniqueKey) {
      dispatchSelectedMonitoringSensorUniqueKey(null);
    }
  });

  return <MonitoringContext value={value}>{children}</MonitoringContext>;
};

export const useMonitoringContext = () => {
  const context = useContext(MonitoringContext);
  if (!context) {
    throw new Error('useMonitoring must be used within a MonitoringProvider');
  }
  return context;
};

export const useSelectedSensorKey = (): string => {
  const { state } = useMonitoringContext();
  if (!state.selectedSensorKey) {
    throw new Error('useSelectedSensorKey used when selectedSensorKey is null');
  }
  return state.selectedSensorKey;
};
